import { ColumnMeta, ColumnMetaDataContext, Entity, EntityKey, EntityType } from '@common/api';
import { Button } from '@components/button';
import { Confirmation } from '@components/confirmation/Confirmation';
import { TristateCheckboxField, CheckboxField, FormField, SearchInputField, SelectInputField } from '@components/form';
import { FlexCol } from '@components/layout/Flex';
import { Modal } from '@components/modal/Modal';
import { useModal } from '@components/modal/ModalProvider';
import { Toggle } from '@components/toggle/Toggle';
import { useEntities, useEntity } from '@data/entity/entity';
import { accountTagToEntityKey, dimensionTagToEntityKey } from '@data/entity/legacy';
import { ENTITY_FILTERS, fetchEntitySearch } from '@data/entity/search';
import { toEntityKey } from '@data/entity/util';
import { useTimePoints } from '@data/hooks/timePoints';
import { useGlobalLoadingContext } from '@features/loading/globalLoadingContext';
import { useDerivedState } from '@hooks/useDerivedState';
import { useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { findBestMatch } from 'string-similarity';
import { useI18nContext } from '../../i18n/i18n-react';
import type { ColumnFilter, DetectedTagMap } from './types';
import { EditTags } from '@features/tags/EditTags';

const NAME_MATCH_THRESHOLD = 0.75;
const LIST_HEIGHT = 300;
const LIST_ITEM_HEIGHT = 28;

const NO_IDS: number[] = [];

interface ColumnEditModalProps {
    importJobEditable: boolean;
    columnMeta: Readonly<ColumnMeta>;
    columnFilter: Readonly<ColumnFilter>;
    updateColumnMeta: (newColumnMeta: Readonly<ColumnMeta>) => Promise<void>;
    setColumnFilter: (newColumnFilter: Readonly<ColumnFilter>) => void;
    getFilterValues: (newColumnMeta: Readonly<ColumnMeta>) => Pick<ColumnFilter, 'values' | 'showValues'>;
}

/**
 * in columnMeta.applyTagsIds we keep member and variable gids in the same array so we
 * use this hook to create corresponding EntityKeys
 * @param ids array of member or variable ids
 * @returns array of EnityKey
 */
function useEntityKeysFromTagIds(ids?: number[]) {
    const tagIds = ids ?? NO_IDS;
    const members = useEntities(tagIds.map(dimensionTagToEntityKey));
    const variables = useEntities(tagIds.map(accountTagToEntityKey));

    return useMemo(() => {
        const entityKeys = [];
        for (const id of tagIds) {
            const member = members.find((m) => m?.id === id);
            if (member) {
                entityKeys.push(toEntityKey(member));
                continue;
            }
            const variable = variables.find((v) => v?.id == id);
            if (variable) {
                entityKeys.push(toEntityKey(variable));
                continue;
            }
        }

        return entityKeys;
    }, [members, variables, tagIds]);
}

export default function ColumnEditModal({
    importJobEditable,
    columnMeta,
    columnFilter,
    updateColumnMeta,
    setColumnFilter,
    getFilterValues,
}: ColumnEditModalProps) {
    const { LL } = useI18nContext();
    const { showModal, hideModal } = useModal();

    const timePoints = useTimePoints().data;
    const timePointOptions = timePoints?.map((tp) => ({ label: `${tp.month}/${tp.year}`, value: tp.id }));

    const [dataContext, setDataContext] = useState<ColumnMetaDataContext>(
        columnMeta?.dataContext ?? ColumnMetaDataContext.NONE
    );

    const [tableTagEntityKey, setTableTagEntityKey] = useState(
        columnMeta?.tableTagId
            ? {
                  type: EntityType.DimensionTable,
                  id: columnMeta.tableTagId,
              }
            : null
    );
    const tableTag = useEntity(tableTagEntityKey);

    const [timePointId, setTimePointId] = useState<number | undefined>(columnMeta?.timePointId);
    const [detectedTagMap, setDetectedTagMap] = useState<DetectedTagMap | undefined>(columnMeta?.detectedTagMap);
    const [manualMapping, setManualMapping] = useState<boolean | undefined>(columnMeta?.manualMapping);

    const applyTagsEntityKeys = useEntityKeysFromTagIds(columnMeta.applyTagsIds);
    const [applyTags, setApplyTags] = useDerivedState<EntityKey[]>(applyTagsEntityKeys, [applyTagsEntityKeys]);

    const [searchText, setSearchText] = useState('');

    const [columnFilterLocal, setColumnFilterLocal] = useDerivedState<ColumnFilter>(() => columnFilter, [columnFilter]);

    const { globalLoadingWrapped } = useGlobalLoadingContext();

    const isSearching = !!searchText.length;
    const showEmptyCellsOption = columnFilterLocal.hasEmptyCells && !isSearching;
    const filteredValues = useMemo(
        () =>
            isSearching
                ? columnFilterLocal.values.filter((v) => v.formatted.toLowerCase().match(searchText.toLowerCase()))
                : columnFilterLocal.values,
        [isSearching, searchText, columnFilterLocal]
    );

    // offset 1 for "Select all", another 1 if "(Blank cells)" should be displayed
    const displayDataOffset = showEmptyCellsOption ? 2 : 1;

    const filterSelectAll = useMemo<boolean | null>(() => {
        const { showEmptyCells, showValues } = columnFilterLocal;

        if (filteredValues.length === 0) {
            // No values are present and no search filter
            if (showEmptyCellsOption) {
                // Empty cells are only option
                return !!showEmptyCells;
            } else {
                // No options in filter
                return null;
            }
        }

        // Check if all shown items have the same checked / unchecked state
        const showFirstValue = !!showValues.get(filteredValues[0].raw);
        const hasDifferentValues = filteredValues.some((v) => showValues.get(v.raw) !== showFirstValue);
        if (hasDifferentValues) return null;

        // Check whether blank cells exist, and if they do whether they match "show" of all items
        // (if we got to here, all items have same "show" as first item)
        if (showEmptyCellsOption && showEmptyCells !== showFirstValue) {
            return null;
        }

        // all filter options have same "show"
        return showFirstValue;
    }, [columnFilterLocal, filteredValues, showEmptyCellsOption]);

    const dataContextOptions = useMemo<{ label: string; value: ColumnMetaDataContext }[]>(
        () => [
            { label: LL.NONE(), value: ColumnMetaDataContext.NONE },
            { label: LL.TABLE(), value: ColumnMetaDataContext.IDENTIFIER },
            { label: LL.VALUE(), value: ColumnMetaDataContext.VALUES },
            { label: LL.DATE(), value: ColumnMetaDataContext.DATETIME },
            { label: LL.VALUE_FOR_MONTH(), value: ColumnMetaDataContext.TIMEPOINT },
            { label: LL.DESCRIPTION(), value: ColumnMetaDataContext.DESCRIPTION },
            { label: LL.CUSTOM_FIELD(), value: ColumnMetaDataContext.CUSTOM },
            { label: LL.TABLE_DESCRIPTION(), value: ColumnMetaDataContext.TAG_DESCRIPTION },
        ],
        [LL]
    );

    const changeDataContext = (newDataContext: ColumnMetaDataContext | null) => {
        if (newDataContext === null) {
            return;
        }
        setDataContext(newDataContext);
        if (newDataContext !== ColumnMetaDataContext.IDENTIFIER) {
            setTableTagEntityKey(null);
            setDetectedTagMap(undefined);
        }
        if (newDataContext !== ColumnMetaDataContext.TIMEPOINT) {
            setTimePointId(undefined);
        }

        setColumnFilterLocal((filter) => {
            const { values, showValues } = getFilterValues({
                ...columnMeta,
                dataContext: newDataContext,
            });
            return {
                ...filter,
                values,
                showValues,
            };
        });
    };

    const changeTimePoint = (targetValue: number | undefined) => {
        const newTimePointId = typeof targetValue === 'number' ? targetValue : undefined;
        setTimePointId(newTimePointId);
    };

    const changeTableTag = async (tags: EntityKey[]) => {
        setTableTagEntityKey(tags.length > 0 ? tags[0] : null);
        if (tags.length > 0) {
            setDetectedTagMap({});
        }
    };

    const toggleSelectAll = () =>
        setColumnFilterLocal((previousColumnFilterLocal) => {
            const { hasEmptyCells, showEmptyCells, values, showValues } = previousColumnFilterLocal;

            // if all values are selected, unselect them all, else select them all
            const setFilterTo = filterSelectAll === true ? false : true;
            const columnFilter: ColumnFilter = {
                hasEmptyCells,
                showEmptyCells: isSearching ? showEmptyCells : setFilterTo,
                values,
                showValues: new Map<string | number, boolean>(showValues),
            };
            for (const value of filteredValues) {
                columnFilter.showValues.set(value.raw, setFilterTo);
            }
            return columnFilter;
        });

    const toggleFilterItem = (filterItem: string | number | null) => {
        setColumnFilterLocal((previousColumnFilterLocal) => {
            const { hasEmptyCells, showEmptyCells, values, showValues } = previousColumnFilterLocal;

            const columnFilter: ColumnFilter = {
                hasEmptyCells,
                showEmptyCells,
                values,
                showValues: new Map<string | number, boolean>(showValues),
            };
            if (filterItem === null) {
                columnFilter.showEmptyCells = !columnFilter.showEmptyCells;
            } else {
                columnFilter.showValues.set(filterItem, !showValues.get(filterItem));
            }
            return columnFilter;
        });
    };

    const handleManualMappingDimensionMemberChange = (members: EntityKey[], itemValue: string | number) => {
        const detectedTagMapCopy = { ...detectedTagMap };
        detectedTagMapCopy[itemValue] = members.map((member) => member.id);
        setDetectedTagMap(detectedTagMapCopy);
    };

    const autodetectDimensions = async () => {
        await globalLoadingWrapped(async () => {
            if (tableTagEntityKey === null) {
                // can't detect dimensions with no table
                return;
            }

            const memberEntities = await fetchEntitySearch(
                '',
                { types: [EntityType.DimensionMember], parents: [tableTagEntityKey] },
                100000
            );

            // // map names and descriptions of dimensions
            const dimensionTagNames = new Array<string>(memberEntities.length);
            const dimensionTagDescriptions = new Array<string>(memberEntities.length);
            for (let i = 0; i < memberEntities.length; i++) {
                dimensionTagNames[i] = memberEntities[i].meta.name.toLowerCase();
                dimensionTagDescriptions[i] = memberEntities[i].meta.description?.toLowerCase() ?? '';
            }

            const detectedTagMapCopy = { ...detectedTagMap };
            for (const value of columnFilterLocal.values) {
                // find best match by name, use formatted value to look for tags
                let bestMemberMatch: Entity | undefined = undefined;
                const bestMatchDirect = findBestMatch(value.formatted.toLowerCase(), dimensionTagNames);
                if (bestMatchDirect.bestMatch.rating > NAME_MATCH_THRESHOLD) {
                    bestMemberMatch = memberEntities[bestMatchDirect.bestMatchIndex];
                } else {
                    // low name matches, try to find best match by description
                    const bestMatchDescription = findBestMatch(value.formatted.toLowerCase(), dimensionTagDescriptions);
                    if (bestMatchDescription.bestMatch.rating > NAME_MATCH_THRESHOLD) {
                        bestMemberMatch = memberEntities[bestMatchDescription.bestMatchIndex];
                    }
                }

                if (bestMemberMatch !== undefined) {
                    // match was found by formatted value
                    // however, save the match by raw value
                    const previousTagIds = detectedTagMapCopy[value.raw];
                    const tagIdsCopy = previousTagIds !== undefined ? [...previousTagIds] : [];
                    if (!tagIdsCopy.includes(bestMemberMatch.id)) {
                        tagIdsCopy.push(bestMemberMatch.id);
                    }
                    detectedTagMapCopy[value.raw] = tagIdsCopy;
                }
            }

            setDetectedTagMap(detectedTagMapCopy);
        });
    };

    const selectOnlyUnmapped = () => {
        setColumnFilterLocal((previousColumnFilterLocal) => {
            const { hasEmptyCells, showEmptyCells } = previousColumnFilterLocal;
            const columnFilter: ColumnFilter = {
                hasEmptyCells,
                showEmptyCells,
                values: [...columnFilterLocal.values],
                showValues: new Map<string | number, boolean>(columnFilterLocal.showValues),
            };

            // check table default value
            const tableHasDefaultValue = tableTag && !!tableTag.dimensionTable?.defaultMemberGid;

            // show empty cells only if there is no default value
            columnFilter.showEmptyCells = !tableHasDefaultValue;

            // go through values in filter
            for (const value of filteredValues) {
                // count detected tags for every value
                const detectedTagCountForValue = detectedTagMap?.[value.raw]?.length ?? 0;
                // show values that don't have any detected tags
                columnFilter.showValues.set(value.raw, detectedTagCountForValue === 0);
            }

            return columnFilter;
        });
    };

    const clearTagMap = () => {
        setDetectedTagMap({});
    };

    const changeManualMapping = (newManualMapping: boolean) => {
        setManualMapping(newManualMapping);
        if (!newManualMapping) {
            setDetectedTagMap(columnMeta?.detectedTagMap);
        }
    };

    const manualMappingHandler = () => {
        const newManualMapping = !manualMapping;
        if (newManualMapping) {
            changeManualMapping(newManualMapping);
        } else {
            showModal(
                <Confirmation
                    title={LL.CONFIRM()}
                    question={LL.REMOVE_ALL_MAPPINGS_CONFIRM()}
                    message={LL.REMOVE_MANUALLY_ADDED_MAPPING_TAGS()}
                    onConfirm={() => {
                        changeManualMapping(newManualMapping);
                        hideModal();
                    }}
                    onCancel={() => hideModal()}
                />
            );
        }
    };

    const saveColumnMetaChanges = async () => {
        await globalLoadingWrapped(async () => {
            const currentColumnMetaCopy: ColumnMeta = {
                name: columnMeta.name,
                dataContext: dataContext,
            };
            if (columnMeta.dateFormat !== undefined) {
                currentColumnMetaCopy.dateFormat = columnMeta.dateFormat;
            }
            if (dataContext === ColumnMetaDataContext.IDENTIFIER) {
                if (tableTagEntityKey !== null) {
                    currentColumnMetaCopy.tableTagId = tableTagEntityKey.id;
                }
                if (detectedTagMap !== undefined) {
                    currentColumnMetaCopy.detectedTagMap = detectedTagMap;
                }
                if (manualMapping !== undefined) {
                    currentColumnMetaCopy.manualMapping = manualMapping;
                }
            } else if (dataContext === ColumnMetaDataContext.TIMEPOINT) {
                if (timePointId !== undefined) {
                    currentColumnMetaCopy.timePointId = timePointId;
                }
                if (applyTags.length > 0) {
                    currentColumnMetaCopy.applyTagsIds = applyTags.map((entity) => entity.id);
                }
            } else if (dataContext === ColumnMetaDataContext.VALUES) {
                if (applyTags.length > 0) {
                    currentColumnMetaCopy.applyTagsIds = applyTags.map((entity) => entity.id);
                }
            } else if (dataContext === ColumnMetaDataContext.TAG_DESCRIPTION) {
                currentColumnMetaCopy.tableTagId = tableTagEntityKey?.id ?? undefined;
                currentColumnMetaCopy.detectedTagMap = {};
                currentColumnMetaCopy.manualMapping = false;
            }
            await updateColumnMeta(currentColumnMetaCopy);
        });
    };

    const saveChanges = async () => {
        if (importJobEditable) {
            await saveColumnMetaChanges();
        }
        setColumnFilter(columnFilterLocal);
        hideModal();
    };

    return (
        <Modal.Content width={700}>
            <Modal.Header title={`${LL.EDIT_COLUMN()} "${columnMeta.name}"`} />
            <Modal.Body className="fs-import-column-edit">
                <form className="form-horizontal" onSubmit={(e) => e.preventDefault()}>
                    <FlexCol $gap={8}>
                        <SelectInputField
                            label={LL.COLUMN_TYPE()}
                            options={dataContextOptions}
                            value={dataContext}
                            onChange={changeDataContext}
                            disabled={!importJobEditable}
                        />
                        {dataContext === ColumnMetaDataContext.TIMEPOINT && (
                            <SelectInputField
                                label={LL.MONTH()}
                                options={timePointOptions ?? []}
                                value={timePointId}
                                onChange={changeTimePoint}
                                disabled={!importJobEditable}
                            />
                        )}
                        {(dataContext === ColumnMetaDataContext.IDENTIFIER ||
                            dataContext === ColumnMetaDataContext.TAG_DESCRIPTION) && (
                            <EditTags
                                kind={LL.TABLE()}
                                type={EntityType.DimensionTable}
                                tags={tableTagEntityKey ? [tableTagEntityKey] : []}
                                onChange={changeTableTag}
                                entityFilter={ENTITY_FILTERS.table}
                                limit={1}
                                canEdit={importJobEditable}
                            />
                        )}

                        {(dataContext === ColumnMetaDataContext.VALUES ||
                            dataContext === ColumnMetaDataContext.TIMEPOINT) && (
                            <FormField label={LL.VARIABLE_AND_DIMENSIONS()}>
                                <EditTags
                                    entityFilter={{ types: [EntityType.DimensionMember, EntityType.Variable] }}
                                    tags={applyTags}
                                    onChange={setApplyTags}
                                    canEdit={importJobEditable}
                                />
                            </FormField>
                        )}

                        {importJobEditable && dataContext === ColumnMetaDataContext.IDENTIFIER && (
                            <div className="fs-filter-toolbar">
                                <Button
                                    type="button"
                                    $color="function"
                                    $size="small"
                                    title={LL.SHOW_UNMAPPED()}
                                    onClick={selectOnlyUnmapped}
                                    style={{ marginRight: 32 }}
                                />
                                <div>
                                    <Toggle
                                        checked={manualMapping}
                                        $size="small"
                                        onChange={manualMappingHandler}
                                        title={LL.MANUAL_MAPPING()}
                                        style={{ marginRight: 4 }}
                                    />
                                    <Button
                                        type="button"
                                        $color="primary"
                                        $size="small"
                                        title={LL.CLEAR()}
                                        onClick={clearTagMap}
                                        style={{ marginRight: 4 }}
                                        disabled={!manualMapping}
                                    />
                                    <Button
                                        type="button"
                                        $color="primary"
                                        $size="small"
                                        title={LL.AUTODETECT()}
                                        onClick={autodetectDimensions}
                                        disabled={!manualMapping}
                                    />
                                </div>
                            </div>
                        )}

                        <SearchInputField
                            label={LL.FILTER()}
                            value={searchText}
                            onChange={setSearchText}
                            placeholder={LL.SEARCH_VALUES()}
                        />

                        <div className="fs-filter-box">
                            <FixedSizeList
                                height={LIST_HEIGHT}
                                itemCount={filteredValues.length + displayDataOffset}
                                itemSize={LIST_ITEM_HEIGHT}
                                width={'100%'}
                                style={{ paddingTop: 10 }}
                                className="tiny-scrollbars"
                            >
                                {({ index, style }) => {
                                    if (index === 0) {
                                        return (
                                            <div style={style}>
                                                <div className="filters-and-tags">
                                                    <TristateCheckboxField
                                                        value={filterSelectAll}
                                                        onChange={toggleSelectAll}
                                                        label={LL.SELECT_ALL_BRACKETS()}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    } else if (showEmptyCellsOption && index === 1) {
                                        return (
                                            <div style={style}>
                                                <div className="filters-and-tags">
                                                    <CheckboxField
                                                        label={LL.BLANK_CELLS_BRACKETS()}
                                                        value={columnFilterLocal.showEmptyCells}
                                                        onChange={() => toggleFilterItem(null)}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        const itemValue = filteredValues[index - displayDataOffset];
                                        return (
                                            <div style={style}>
                                                <div className="filters-and-tags">
                                                    <CheckboxField
                                                        label={itemValue.formatted}
                                                        value={!!columnFilterLocal.showValues.get(itemValue.raw)}
                                                        onChange={() => toggleFilterItem(itemValue.raw)}
                                                    />
                                                    {dataContext === ColumnMetaDataContext.IDENTIFIER &&
                                                        tableTagEntityKey !== null && (
                                                            <EditTags
                                                                kind={LL.DIMENSION_MEMBER()}
                                                                entityFilter={{
                                                                    ...ENTITY_FILTERS.member,
                                                                    parents: [tableTagEntityKey],
                                                                }}
                                                                tags={
                                                                    detectedTagMap?.[itemValue.raw]?.map(
                                                                        dimensionTagToEntityKey
                                                                    ) ?? []
                                                                }
                                                                onChange={(tags) =>
                                                                    handleManualMappingDimensionMemberChange(
                                                                        tags,
                                                                        itemValue.raw
                                                                    )
                                                                }
                                                                canEdit={importJobEditable && manualMapping}
                                                            />
                                                        )}
                                                </div>
                                            </div>
                                        );
                                    }
                                }}
                            </FixedSizeList>
                        </div>
                    </FlexCol>
                </form>
            </Modal.Body>
            <Modal.Footer doneLabel={LL.SAVE()} done={saveChanges} cancelLabel={LL.CANCEL()} />
        </Modal.Content>
    );
}
